import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ServiceDetailPageContainer } from './ServiceDetailPage';
import { FaPaperPlane } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const ServiceImage = styled.img`
  max-width: 200px; /* Smaller size */
  height: auto;
  border-radius: 0; /* Remove border-radius */
  margin: 0.5rem 0 1rem 2rem; /* Adjusted margin for text wrapping */
  box-shadow: none; /* Remove box-shadow */
  float: right; /* Allow text to wrap around */

  @media (max-width: 768px) {
    float: none;
    margin: 1rem auto;
    max-width: 80%;
    display: block;
  }
`;

const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

export const ContactTitle = styled.h2`
  color: #fff !important;
  margin-top: 0 !important;
`;

const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled.button`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

function MobileApp() {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow numbers and limit to exactly 11 digits
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setFormData({
          ...formData,
          [name]: numbersOnly
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: formData.name,
        phone: formData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setFormData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceDetailPageContainer>
      <h1>Mobil Uygulama Geliştirme</h1>
      <p>İşletmenize özel, kullanıcı dostu ve yenilikçi mobil uygulamalar geliştiriyoruz. iOS ve Android platformları için modern teknolojilerle, iş süreçlerinizi optimize eden ve müşteri etkileşimini artıran çözümler sunuyoruz.</p>

      <ServiceImage src="/media/demirer-ajans-6.png" alt="Mobil Uygulama Görseli 1" />

      <h2>Neden Mobil Uygulama Geliştirme Önemlidir?</h2>
      <p>Mobil uygulamalar, günümüz dünyasında müşteri erişimini ve marka sadakatini artırmanın en etkili yollarından biridir. Kullanıcıların akıllı telefonlarında sürekli erişilebilir bir varlık olmak, doğrudan pazarlama kanalları oluşturur ve işletmenizin dijital ayak izini güçlendirir.</p>

      <ServiceImage src="/media/demirer-ajans-7.png" alt="Mobil Uygulama Görseli 2" />

      <h3>Hizmetlerimiz Neleri Kapsar?</h3>
      <ul>
        <li><b>Platforma Özel (Native) Uygulama Geliştirme:</b> iOS (Swift/Objective-C) ve Android (Java/Kotlin) için yüksek performanslı ve optimize edilmiş uygulamalar.</li>
        <li><b>Hibrit Uygulama Geliştirme:</b> React Native, Flutter gibi frameworkler kullanarak tek bir kod tabanıyla her iki platformda da çalışan uygulamalar.</li>
        <li><b>Kullanıcı Arayüzü (UI) ve Kullanıcı Deneyimi (UX) Tasarımı:</b> Sezgisel, estetik ve kullanıcı dostu arayüzler tasarlama.</li>
        <li><b>Backend Geliştirme ve API Entegrasyonu:</b> Uygulamanın veritabanı, sunucu ve diğer sistemlerle sorunsuz entegrasyonu.</li>
        <li><b>Test ve Kalite Güvencesi:</b> Uygulamanın tüm fonksiyonlarının hatasız çalıştığından emin olmak için kapsamlı testler.</li>
        <li><b>Yayınlama ve Destek:</b> Uygulamayı App Store ve Google Play'de yayınlama süreçlerini yönetme ve sonrasında teknik destek sağlama.</li>
        <li><b>Güncelleme ve Bakım:</b> Uygulamanın performansını ve güvenliğini sağlamak için düzenli güncellemeler ve bakım hizmetleri.</li>
      </ul>

      <h2>Mobil Uygulama Geliştirme Sürecimiz</h2>
      <p>Mobil uygulama geliştirme sürecimiz, ihtiyaçlarınızı analiz etmekle başlar. Uzman ekibimiz, işletmenizin hedeflerini ve kullanıcı beklentilerini anlayarak, en uygun çözümü sunar. Her aşamada sizi bilgilendiriyor, geri bildirimlerinizi alıyor ve uygulamanızı mükemmelleştiriyoruz.</p>
      <p>Modern teknolojiler ve yenilikçi yaklaşımlarla, kullanıcı dostu ve performanslı mobil uygulamalar geliştiriyoruz. İşletmenizin dijital varlığını güçlendirmek için bize güvenebilirsiniz.</p>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm onSubmit={handleSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={formData.name}
              onChange={handleInputChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={formData.phone}
              onChange={handleInputChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ServiceDetailPageContainer>
  );
}

export default MobileApp; 